import { useEffect, useState } from "react";
import { request, mergeObjects } from "@/utils";
import { useGlobalStore } from '@/hooks';
import Cookies from "js-cookie";

/**
 * 错误响应格式
 */
interface ErrorResponse {
  code?: number;
  message?: string;
}

/**
 * 请求 Hook 返回值
 */
export interface UseRequestResult {
  data?: any;
  pageInfo?: any;
  more?: any;
  loading: boolean;
  error?: ErrorResponse;
  status?: any;
  /**
   * 手动触发请求
   * @param options 请求配置选项
   */
  send: (params?: RequestOptions["params"], nOptions?: Omit<RequestOptions, 'method' | 'url' | 'params' | 'manual'>) => Promise<UseRequestResult>;
}

/**
 * 请求配置选项
 */
interface RequestOptions {
  method?: 'get' | 'post' | 'put';
  url?: string;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  manual?: boolean;
  isTips?: boolean | (string | number)[];
  cancelPrevious?: boolean,
  uncached?: boolean;
}

type UseRequestParams = [string | RequestOptions, Record<string, unknown>?, RequestOptions?];

const normalizeParameters = (...requestParams: UseRequestParams): RequestOptions => {

  const [firstParam, secondParam, thirdParam] = requestParams;
  const params: RequestOptions = {
    params: {}
  };

  if (typeof firstParam === 'string') {
    params.url = firstParam;
  } else {
    Object.assign(params, firstParam);
  }
  if (secondParam) {
    params.params = secondParam;
  }
  if (thirdParam) {
    Object.assign(params, thirdParam);
  }
  return params;
};

const getBaseHeaders = (appConfig: any) => {

  let windowAppConfig: any = {};
  if (process.browser) {
    windowAppConfig = window?.__WEB_SITE_APP_CONFIG?.appConfig
  }

  // 当 host 用 orgIdByEnv map 跨环境共享一份配置时，按当前 env 选 orgId。
  // env 来自 host 显式设置或 request.tsx 顶层从 script src 推断回写的值。
  const effectiveEnv = appConfig?.env || windowAppConfig?.env;
  const orgIdFromMap = effectiveEnv
    ? (appConfig?.orgIdByEnv?.[effectiveEnv] || windowAppConfig?.orgIdByEnv?.[effectiveEnv])
    : undefined;

  return {
    orgId: appConfig?.orgId || windowAppConfig?.orgId || orgIdFromMap,
    tid: appConfig?.tid || windowAppConfig?.tid,
    channel: appConfig?.channel || windowAppConfig?.channel || 'Web'
  }
}

/**
 * 请求 Hook，用于实现接口请求逻辑
 * @returns Hook 返回值
 */
const useRequest = (...requestParams: UseRequestParams): UseRequestResult => {

  const { appConfig } = useGlobalStore();

  const resetParams = (): RequestOptions => {
    const params: RequestOptions = normalizeParameters(...requestParams);

    const baseHeaders = getBaseHeaders(appConfig);
    
    params.headers = {...params.headers || {}, ...baseHeaders};

    if (!params?.method) {
      params.method = 'get';
    }
    if (params.manual === undefined) {
      params.manual = true;
    }
    return params;
  }

  const options: RequestOptions = resetParams();

  const { manual } = options;

  const [data, setData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorResponse | {}>({});

  useEffect(() => {
    if (!manual) {
      fetchData(options);
    }
  }, [])

  /**
   * 请求数据
   * @param options 请求配置选项
   */
  const fetchData = async (_options?: RequestOptions): Promise<any> => {

    const __options: any = mergeObjects(options as any, _options as any);
    if (!__options || !__options.url) {
      throw new Error("useRequest: missing url!");
    }
    setLoading(true);
    try {
      const sessionid = Cookies.get('__session_id');
      const responseData = await request[options.method || "get"](
        __options.url,
        __options.params || {},
        { 
          Sessionid: sessionid,
          ...__options.headers
        },
        {isTips: __options.isTips === undefined ? true : __options.isTips, cancelPrevious: __options?.cancelPrevious === undefined ? false : __options?.cancelPrevious}
      );
      if (!__options.uncached) {
        setData(responseData);
      }
      setLoading(false);
     
      return {
        data: responseData?.result,
        status: responseData.status,
        more: responseData.more
      };

    } catch (error: any | unknown) {
      console.error(error)
      setError(error);
      if (error?.message === '重复请求') {
        setLoading(true)
        throw error;
      }
      setLoading(false)
      throw error;
    }
  };

  /**
   * 手动触发请求
   * @param params
   * @param __options
   */
  const send = async (params?: RequestOptions["params"], __options?: Omit<RequestOptions, 'method' | 'url' | 'params' | 'manual'>): Promise<UseRequestResult> => {

    let config: any = { ...options };
    
    config = Object.assign(config, __options || {});

    let newParams = params;

    if (options.params && typeof newParams === 'object' && !Array.isArray(newParams)) {
      newParams = Object.assign(newParams, options.params);
    }
    config.params = newParams || {};
    config.otherParams = {
      cancelPrevious: newParams?.cancelPrevious === undefined ? false : config?.cancelPrevious,
      isTips: newParams?.isTips === undefined ? true : config?.isTips,
    }
    return await fetchData(config);
  };

  const result: UseRequestResult = {
    data: data?.result,
    error,
    send,
    loading
  }

  if (data?.more) {
    result.more = data?.more;
  }

  if (data?.pageInfo) {
    result.pageInfo = data.pageInfo;
  }

  return result;
};

export default useRequest;

